import { useDebouncedStableCallback } from "@app/shared/utils/debounce";
import { Icon } from "@design-system/Icon";
import { Text } from "@design-system/Typography/Text";
import { cn } from "@design-system/Utilities";
import { type MutationStatus } from "@tanstack/query-core";
import { useEffect, useState, type FC } from "react";
import { useAutosaveDisplayTranslations } from "./i18n";

export const AutosaveDisplay: FC<{ statuses: MutationStatus[] }> = ({
    statuses,
}) => {
    const { t, keys } = useAutosaveDisplayTranslations();
    const [displayStatus, setDisplayStatus] = useState<
        "pending" | "success" | null
    >(null);
    const latestStatus = statuses[statuses.length - 1];

    const debouncedShowSaved = useDebouncedStableCallback(() => {
        setDisplayStatus("success");
    }, 3000);

    const debouncedHide = useDebouncedStableCallback(() => {
        setDisplayStatus(null);
    }, 5000);

    useEffect(() => {
        if (latestStatus === "pending") return setDisplayStatus("pending");
        if (latestStatus === "success") {
            debouncedShowSaved();
            return debouncedHide();
        }
        setDisplayStatus(null);
    }, [latestStatus, debouncedShowSaved, debouncedHide, setDisplayStatus]);

    return displayStatus ? (
        <Text
            className={cn(
                "flex items-center gap-1 transition-all duration-200",
                displayStatus === "pending" && "animate-pulse",
            )}
            variant="body-sm"
            color="tertiary"
        >
            <Icon
                name={displayStatus === "pending" ? "arrowSaving" : "check"}
                size="sm"
                className="text-tertiary"
            />
            {t(keys.autosave_display, { context: displayStatus })}
        </Text>
    ) : null;
};
